/**
 * Infrastructure Icons Registry
 *
 * Provides infrastructure icons from local SVG assets.
 * These icons use the same color (#8B5CF6) as AD Site icon for visual consistency.
 * Uses explicit imports with SVGR for optimal tree-shaking.
 */

import type { ComponentType, ReactNode } from 'react';

// Explicit SVG imports with SVGR
import InfrastructureComputer from '@/sections/attacks/attackPaths/components/icons/bundles/infrastructure/infrastructure-computer.svg?react';
import InfrastructureDatabase from '@/sections/attacks/attackPaths/components/icons/bundles/infrastructure/infrastructure-database.svg?react';
import InfrastructureFirewall from '@/sections/attacks/attackPaths/components/icons/bundles/infrastructure/infrastructure-firewall.svg?react';
import InfrastructureNetwork from '@/sections/attacks/attackPaths/components/icons/bundles/infrastructure/infrastructure-network.svg?react';
import InfrastructureRouter from '@/sections/attacks/attackPaths/components/icons/bundles/infrastructure/infrastructure-router.svg?react';
import InfrastructureServer from '@/sections/attacks/attackPaths/components/icons/bundles/infrastructure/infrastructure-server.svg?react';
import InfrastructureWebApplication from '@/sections/attacks/attackPaths/components/icons/bundles/infrastructure/infrastructure-web-application.svg?react';
import type {
  IconDefinition,
  IconRenderer,
  IconRenderProps,
} from '@/sections/attacks/attackPaths/components/icons/registry/icon-renderers-map';
import { iconRenderers } from '@/sections/attacks/attackPaths/components/icons/registry/icon-renderers-map';
import { INFRASTRUCTURE_ICONS } from '@/sections/attacks/attackPaths/components/icons/types';

// ============================================================================
// Icon Component Type
// ============================================================================

type InfrastructureIconComponent = ComponentType<{
  className?: string;
  width?: string | number;
  height?: string | number;
}>;

// ============================================================================
// Fallback Component
// ============================================================================

const FallbackIcon = ({ className }: { className?: string }) => (
  <span className={className}>?</span>
);

// ============================================================================
// Icon Component Map
// ============================================================================

/**
 * Explicit mapping from INFRASTRUCTURE_ICONS names to SVGR components.
 */
const infrastructureIconComponents: Partial<
  Record<(typeof INFRASTRUCTURE_ICONS)[number], InfrastructureIconComponent>
> = {
  Network: InfrastructureNetwork,
  Computer: InfrastructureComputer,
  Server: InfrastructureServer,
  Firewall: InfrastructureFirewall,
  WebApplication: InfrastructureWebApplication,
  Router: InfrastructureRouter,
  Database: InfrastructureDatabase,
};

/**
 * Human-readable labels for infrastructure icons.
 */
const infrastructureIconLabels: Partial<
  Record<(typeof INFRASTRUCTURE_ICONS)[number], string>
> = {
  Network: 'Network/Subnet',
  Computer: 'Computer/Endpoint',
  Server: 'Server',
  Firewall: 'Firewall/Security',
  WebApplication: 'Web Application',
  Router: 'Router/Gateway',
  Database: 'Database/RDS',
};

// ============================================================================
// Infrastructure Renderer Implementation
// ============================================================================

/**
 * Infrastructure icon renderer for the attack graph icon system.
 */
const infrastructureRenderer: IconRenderer = {
  render: (
    name: string,
    { size = 24, className }: IconRenderProps
  ): ReactNode => {
    const IconComponent =
      infrastructureIconComponents[
        name as (typeof INFRASTRUCTURE_ICONS)[number]
      ];
    if (!IconComponent) return null;
    return <IconComponent className={className} width={size} height={size} />;
  },

  validate: (name: string): boolean => {
    return (INFRASTRUCTURE_ICONS as readonly string[]).includes(name);
  },

  getIcon: (name: string) => {
    const IconComponent =
      infrastructureIconComponents[
        name as (typeof INFRASTRUCTURE_ICONS)[number]
      ];
    if (!IconComponent) return null;

    return function WrappedInfrastructureIcon({
      size = 24,
      className,
    }: {
      size?: number;
      className?: string;
    }) {
      return <IconComponent className={className} width={size} height={size} />;
    };
  },

  getAll: (): IconDefinition[] =>
    INFRASTRUCTURE_ICONS.map(name => {
      const IconComponent = infrastructureIconComponents[name];
      return {
        id: `infrastructure:${name}`,
        component: IconComponent ?? FallbackIcon,
        label: infrastructureIconLabels[name] ?? name,
        category: 'infrastructure',
      };
    }),
};

// ============================================================================
// Register Renderer
// ============================================================================

iconRenderers.set('infrastructure', infrastructureRenderer);

export { infrastructureRenderer };
